import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogType, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import clsx from 'clsx';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { Label } from 'office-ui-fabric-react/lib/Label';
import appStyles from '../../App.style';
import styles from './ContractApprovalDialog.style';

const rejectionValidationSchema = Yup.object().shape({
  rejectReason: Yup.string()
    .required('Reason is required')
    .test('empty-check', 'Reason cannot be empty', reason => {
      if (!reason || reason.trim().length === 0) {
        return false;
      }

      return true;
    }),
});

const ContractApprovalDialog = ({
  onSubmit,
  isApproved,
  onDismiss,
  isSubmitting,
  isOpen,
  isEdit,
  error: submitError,
  className,
}) => {
  const submitHandler = async values => {
    const formValues = {
      isApproved,
      rejectReason: values.rejectReason,
    };

    await onSubmit(formValues);
  };

  const validationSchema = !isApproved ? rejectionValidationSchema : null;
  const initialValues = {
    rejectReason: '',
    isApproved,
  };

  const submitText = isApproved ? 'Approve' : 'Reject';
  const contractText = isEdit ? 'edits' : 'contract';
  const message = isApproved
    ? `Once approved, the ${contractText} must be resubmitted if additional changes are necessary.`
    : `Please describe why the ${contractText} will be rejected.`;

  return (
    <Dialog
      dialogContentProps={{
        type: DialogType.normal,
        title: `${submitText} ${contractText}?`,
        closeButtonAriaLabel: 'Close',
      }}
      modalProps={{
        className: clsx(styles.root, className),
      }}
      hidden={!isOpen}
      onDismiss={onDismiss}
      onSubmit={onSubmit}
      isSubmitting={isSubmitting}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={async values => {
          await submitHandler(values);
        }}
        validationSchema={validationSchema}
      >
        {({ errors, touched, values, handleChange }) => (
          <Form>
            <Label className={styles.approvalLabel}>{message}</Label>

            {!isApproved && (
              <div>
                <Field
                  id="rejectReason"
                  name="rejectReason"
                  component={TextField}
                  disabled={isSubmitting}
                  value={values.rejectReason}
                  onChange={handleChange}
                  required
                  data-testid="contract-reject-reason"
                />
                {errors.rejectReason && touched.rejectReason && (
                  <span className={clsx(styles.secondaryIcon, appStyles.defaultColor)}>
                    {errors.rejectReason}
                  </span>
                )}
              </div>
            )}

            {submitError && (
              <span className={clsx(styles.secondaryIcon, appStyles.defaultColor)}>
                {submitError.message}
              </span>
            )}

            <DialogFooter>
              <DefaultButton
                data-testid="approval-cancel-button"
                onClick={onDismiss}
                disabled={isSubmitting}
              >
                Cancel
              </DefaultButton>
              <PrimaryButton
                type="submit"
                data-testid="approval-submit-button"
                disabled={isSubmitting}
              >
                {submitText}
                {isSubmitting && (
                  <Spinner
                    data-testid="contract-approval-spinner"
                    size={SpinnerSize.medium}
                    className={styles.loadingSpinner}
                  />
                )}
              </PrimaryButton>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

ContractApprovalDialog.propTypes = {
  onDismiss: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  error: PropTypes.object,
  isSubmitting: PropTypes.bool,
  isOpen: PropTypes.bool,
  className: PropTypes.string,
  isApproved: PropTypes.bool.isRequired,
  isEdit: PropTypes.bool.isRequired,
};

ContractApprovalDialog.defaultProps = {
  error: {},
  isSubmitting: false,
  isOpen: false,
  className: 'normal',
};

export default ContractApprovalDialog;
